import {
  ICustomer,
  ICustomerWithOrderCount,
  CustomersClient,
  CustomerType,
} from "../clients/CustomersClient";
import { create } from "zustand";

export type CustomersStoreState = {
  customers: ICustomer[];
  loading: boolean;
  error: Error | null;
  getCustomersByOrganizationId: (
    organization_id: string,
    useLoading?: boolean
  ) => Promise<void>;
  getCustomerById: (customer_id: string) => Promise<ICustomer | null>;
  createCustomer: (
    customer: Omit<ICustomer, "id" | "created_at" | "updated_at" | "deleted_at">
  ) => Promise<ICustomer>;
  updateCustomer: (
    customer: Omit<ICustomer, "created_at" | "updated_at" | "deleted_at">
  ) => Promise<void>;
  deleteCustomer: (customer_id: string) => Promise<void>;
  findOrCreateCustomer: (customerData: {
    organization_id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    external_id?: string | null;
    customer_type?: CustomerType;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }) => Promise<ICustomer>;
  searchCustomers: (
    organization_id: string,
    searchTerm: string
  ) => Promise<ICustomer[]>;
  getCustomersWithOrderStats: (
    organization_id: string
  ) => Promise<ICustomerWithOrderCount[]>;
};

export const useCustomersStore = create<CustomersStoreState>((set, get) => ({
  customers: [],
  loading: true,
  error: null,

  async getCustomersByOrganizationId(
    organization_id: string,
    useLoading = true
  ) {
    if (useLoading) {
      set({ loading: true });
    }
    try {
      const customers = await CustomersClient.getCustomersByOrganizationId(
        organization_id
      );
      set({ customers, error: null });
    } catch (error) {
      console.error("Error loading customers:", error);
      set({ error: error as Error });
    } finally {
      if (useLoading) {
        set({ loading: false });
      }
    }
  },

  async getCustomerById(customer_id: string): Promise<ICustomer | null> {
    try {
      // First check if we have it in the store
      const existingCustomer = get().customers.find((c) => c.id === customer_id);
      if (existingCustomer) {
        return existingCustomer;
      }

      // If not, fetch from API
      const customer = await CustomersClient.getCustomerById(customer_id);
      set((state) => ({
        ...state,
        customers: [
          customer,
          ...state.customers.filter((c) => c.id !== customer_id),
        ],
      }));
      return customer;
    } catch (error) {
      console.error("Error fetching customer:", error);
      set({ error: error as Error });
      return null;
    }
  },

  async createCustomer(
    customer: Omit<ICustomer, "id" | "created_at" | "updated_at" | "deleted_at">
  ): Promise<ICustomer> {
    set({ loading: true });
    try {
      const newCustomer = await CustomersClient.createCustomer(customer);
      set((state) => ({
        ...state,
        customers: [newCustomer, ...state.customers],
        error: null,
      }));
      return newCustomer;
    } catch (error) {
      console.error("Error creating customer:", error);
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  async updateCustomer(
    customer: Omit<ICustomer, "created_at" | "updated_at" | "deleted_at">
  ) {
    set({ loading: true });
    try {
      const updatedCustomer = await CustomersClient.updateCustomer(customer);
      set((state) => ({
        ...state,
        customers: state.customers.map((c) =>
          c.id === updatedCustomer.id ? updatedCustomer : c
        ),
        error: null,
      }));
    } catch (error) {
      console.error("Error updating customer:", error);
      set({ error: error as Error });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  async deleteCustomer(customer_id: string) {
    set({ loading: true });
    try {
      await CustomersClient.deleteCustomer(customer_id);
      // Soft delete - remove from active customers list
      set((state) => ({
        ...state,
        customers: state.customers.filter((c) => c.id !== customer_id),
        error: null,
      }));
    } catch (error) {
      console.error("Error deleting customer:", error);
      set({ error: error as Error });
    } finally {
      set({ loading: false });
    }
  },

  async findOrCreateCustomer(customerData: {
    organization_id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    external_id?: string | null;
    customer_type?: CustomerType;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }): Promise<ICustomer> {
    try {
      const customer = await CustomersClient.findOrCreateCustomer(customerData);

      // Update store with the customer (whether it was found or created)
      set((state) => {
        const existingIndex = state.customers.findIndex(
          (c) => c.id === customer.id
        );
        if (existingIndex >= 0) {
          // Update existing customer in store
          const updatedCustomers = [...state.customers];
          updatedCustomers[existingIndex] = customer;
          return {
            ...state,
            customers: updatedCustomers,
            error: null,
          };
        } else {
          // Add new customer to store
          return {
            ...state,
            customers: [customer, ...state.customers],
            error: null,
          };
        }
      });

      return customer;
    } catch (error) {
      console.error("Error finding or creating customer:", error);
      set({ error: error as Error });
      throw error;
    }
  },

  async searchCustomers(organization_id: string, searchTerm: string) {
    try {
      const customers = await CustomersClient.searchCustomers(
        organization_id,
        searchTerm
      );
      return customers;
    } catch (error) {
      console.error("Error searching customers:", error);
      set({ error: error as Error });
      return [];
    }
  },

  async getCustomersWithOrderStats(organization_id: string) {
    set({ loading: true });
    try {
      const customers = await CustomersClient.getCustomersWithOrderStats(
        organization_id
      );
      // Replace store with fresh customer data (without order stats in the main store)
      // This ensures we don't have stale data from deleted customers
      const updatedCustomers = customers.map((customerWithStats) => {
        const { ...customer } = customerWithStats;
        return customer;
      });

      set({
        customers: updatedCustomers,
        error: null,
      });

      return customers;
    } catch (error) {
      console.error("Error fetching customers with order stats:", error);
      set({ error: error as Error });
      return [];
    } finally {
      set({ loading: false });
    }
  },
}));
